import * as React from "react";
import type { Enum } from "@plans/appkit";
import { EyeIcon } from "@plans/icons/untitled-ui/EyeIcon";
import { EyeOffIcon } from "@plans/icons/untitled-ui/EyeOffIcon";
import { StatusCodes } from "http-status-codes";
import { useTranslation } from "react-i18next";
import { Form, redirect, useNavigation, useSearchParams } from "react-router";
import { successResponse, unauthorizedResponse } from "~/api/index.server";
import { safeErrorToErrorResponse } from "~/api/mappings.server";
import { Button } from "~/components/atoms/Button";
import { FormControlErrorMessage } from "~/components/atoms/FormControl";
import { TextInput } from "~/components/atoms/TextInput";
import { PlansLogoIcon } from "~/components/icons/PlansLogoIcon";
import { GeneralErrorBoundary } from "~/components/molecules/GeneralErrorBoundary";
import type { SupportedLocale } from "~/i18n/config";
import { i18nCookie, serverI18n } from "~/i18n/config.server";
import { useToast } from "~/lib/ui/ToastContext";
import { useEffectOnce } from "~/lib/ui/useEffectOnce";
import { z } from "~/lib/validation/helpers";
import { AuthService } from "~/services/auth/service.server";
import { createServiceOptions } from "~/services/common/helpers.server";
import { BaseServiceError } from "~/services/common/index.server";
import {
	authenticateUserPass,
	commitAuthSession,
	getAuthenticatedUser,
	getAuthSession,
	SESSION_USER_KEY,
} from "~/sessions/auth-session.server";
import { SearchParamsEnum as AppLayoutSearchParamsEnum } from "../_app.$slug";
import type { Route } from "./+types/index";

export const SearchParamsEnum: Enum<
	| "emailSuccessfullyVerified"
	| "email"
	| "accountCreated"
	| "passwordSuccessfullyReset"
	| "redirectTo"
> = {
	ACCOUNT_CREATED: "accountCreated",
	EMAIL: "email",
	EMAIL_SUCCESSFULLY_VERIFIED: "emailSuccessfullyVerified",
	PASSWORD_SUCCESSFULLY_RESET: "passwordSuccessfullyReset",
	REDIRECT_TO: "redirectTo",
};

// NB: If experiencing issues with non-localized toasts for initial redirects from login,
// ensure that the `handle.i18n` array includes the necessary translation namespaces.
export const handle = {
	i18n: [
		"common",
		"navigation",
		"leads-overview",
		"command-menu",
		"common",
		"leads-glossary",
		"homes-glossary",
		"login",
		"signup",
		"validation",
	],
};
const loginSchema = z.object({
	intent: z.literal("login"),
	email: z.text(z.email()),
	password: z.text(z.string().min(8)),
	firstLogin: z.booleanString(),
});
const resendVerificationEmailSchema = z.object({
	intent: z.literal("resend-verification-email"),
	email: z.text(z.email()),
});
const schema = z.formData(
	z.discriminatedUnion("intent", [loginSchema, resendVerificationEmailSchema]),
);

export async function action({ request, context, params }: Route.ActionArgs) {
	try {
		// `authenticateUserPass` reads the request body, so clone the request
		// here to parse the form data for intent routing.
		const form = await request.clone().formData();
		const validatedForm = schema.parse(form);

		if (validatedForm.intent === "resend-verification-email") {
			const serviceOptions = await createServiceOptions({
				request,
				context,
				params,
			});
			const authService = new AuthService(serviceOptions);

			await authService.resendEmailVerificationEmail(validatedForm.email);

			return successResponse({ emailVerificationEmailResent: true });
		}

		// Verify credentials and build the user. Throws on bad credentials;
		// the catch below maps a 401 to a friendly error. See
		// `auth-session.server.ts` for the verify implementation.
		const user = await authenticateUserPass(request);

		const headers = new Headers();

		// Store the user on the auth session and on the context
		const session = await getAuthSession(request);
		session.set(SESSION_USER_KEY, user);
		headers.append("Set-Cookie", await commitAuthSession(session));
		context.user = user;

		const cookieLocale = await i18nCookie.parse(request.headers.get("cookie"));

		if (!cookieLocale) {
			const languageCode = user.me.installerCompany
				.defaultLanguageCode satisfies SupportedLocale;

			headers.append("Set-Cookie", await i18nCookie.serialize(languageCode));
		}

		if (user.redirectTo) {
			return redirect(user.redirectTo, { headers });
		}

		if (validatedForm.firstLogin) {
			return redirect(
				`/${user.me.installerCompany.slug}?${AppLayoutSearchParamsEnum.FIRST_LOGIN}=true`,
				{
					headers,
				},
			);
		}

		return redirect(`/${user.me.installerCompany.slug}`, { headers });
	} catch (error) {
		// Re-throw redirects (e.g. from refresh-token flows) without remapping.
		if (error instanceof Response) {
			throw error;
		}
		const t = await serverI18n.getFixedT(request, ["common", "login"]);
		// Invalid credentials surface as a `BaseServiceError` with a 401 status
		// straight from `AuthService.login`.
		if (
			error instanceof BaseServiceError &&
			error.status === StatusCodes.UNAUTHORIZED
		) {
			return unauthorizedResponse({
				...error,
				message: t("common:wrong_credentials_error_title"),
			});
		}
		return safeErrorToErrorResponse(error, t("common:generic_error_title"));
	}
}

export async function loader({ request }: Route.LoaderArgs) {
	const user = await getAuthenticatedUser(request);

	if (user) {
		return redirect(`/${user.me.installerCompany.slug}`);
	}

	return successResponse({});
}

export default function LoginRoute({ actionData }: Route.ComponentProps) {
	const [searchParams] = useSearchParams();
	const { t } = useTranslation(["login", "common", "leads-glossary"]);
	const navigation = useNavigation();
	const [isShowingPassword, setIsShowingPassword] = React.useState(false);

	const { toast } = useToast();

	useEffectOnce(() => {
		if (searchParams.get(SearchParamsEnum.ACCOUNT_CREATED)) {
			toast({
				heading: t("login:account_created_toast.heading"),
				body: t("login:account_created_toast.body"),
				variant: "success",
			});
		}
	});

	useEffectOnce(() => {
		if (searchParams.get(SearchParamsEnum.EMAIL_SUCCESSFULLY_VERIFIED)) {
			toast({
				heading: t("login:email_verified_toast.heading"),
				body: t("login:email_verified_toast.body"),
				variant: "success",
			});
		}
	});

	useEffectOnce(() => {
		if (searchParams.get(SearchParamsEnum.PASSWORD_SUCCESSFULLY_RESET)) {
			toast({
				heading: t("login:password_reset_successfully_toast.heading"),
				body: t("login:password_reset_successfully_toast.body"),
				variant: "success",
			});
		}
	});

	React.useEffect(() => {
		if (actionData?.body?.emailVerificationEmailResent) {
			toast({
				heading: t("login:verification_email_sent_toast.heading"),
				body: t("login:verification_email_sent_toast.body"),
				variant: "success",
			});
		}
	}, [actionData?.body?.emailVerificationEmailResent, t, toast]);

	const searchParamsEmail = searchParams.get(SearchParamsEnum.EMAIL);
	const emailDefaultValue = searchParamsEmail
		? decodeURIComponent(searchParamsEmail)
		: "";
	const [email, setEmail] = React.useState(emailDefaultValue);

	// We only want to display the overall form error message
	// if there are no field specific errors.
	const isFormErrorActive = Boolean(
		actionData?.error?.message && !actionData?.error.issues,
	);

	return (
		<section className="bg-background-soft flex min-h-screen w-full items-center justify-center p-5 sm:p-6">
			<div className="border-background-medium flex w-full max-w-[380px] flex-col items-center rounded-md border bg-white px-5 py-9 sm:px-6">
				<div className="w-full">
					<PlansLogoIcon className="mx-auto h-7" />
					<Form method="post" className="mt-5" id="login-form">
						<h1 className="text-center text-xl font-bold">
							{t("login:title")}
						</h1>
						<input
							type="hidden"
							name="redirectTo"
							value={
								searchParams.get(SearchParamsEnum.REDIRECT_TO) ?? undefined
							}
						/>
						<input
							type="hidden"
							name="firstLogin"
							value={
								searchParams.get(SearchParamsEnum.ACCOUNT_CREATED) ||
								searchParams.get(SearchParamsEnum.EMAIL_SUCCESSFULLY_VERIFIED)
									? "true"
									: "false"
							}
						/>
						<fieldset
							data-cy="login-fieldset"
							disabled={navigation.state !== "idle"}
							className="mt-7 flex flex-col gap-y-3"
						>
							<TextInput
								type="email"
								name="email"
								placeholder={t("leads-glossary:email.placeholder")}
								isRequired
								value={email}
								onChange={(event) => setEmail(event.target.value)}
								isInvalid={Boolean(actionData?.error?.issues?.email)}
								errorMessage={actionData?.error?.issues?.email}
								label={t("leads-glossary:email.label")}
							/>
							<TextInput
								type={isShowingPassword ? "text" : "password"}
								name="password"
								placeholder="********"
								isRequired
								isInvalid={Boolean(actionData?.error?.issues?.password)}
								errorMessage={actionData?.error?.issues?.password}
								label={t("login:password_label")}
								minLength={8}
								maxLength={72}
								suffixIcon={
									<Button
										variant="text"
										className="pointer-events-auto h-4 w-4 touch-auto"
										isIconOnly={true}
										onClick={() => setIsShowingPassword(!isShowingPassword)}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												setIsShowingPassword(!isShowingPassword);
												event.preventDefault();
											}
										}}
									>
										{isShowingPassword ? (
											<EyeOffIcon className="h-4 w-4" />
										) : (
											<EyeIcon className="h-4 w-4" />
										)}
									</Button>
								}
							/>
							<Button
								variant="primary"
								size="large"
								className={"mt-4"}
								type="submit"
								name="intent"
								value="login"
								isDisabled={navigation.state !== "idle"}
								isLoading={navigation.state !== "idle"}
								testId="login-submit"
							>
								{t("login:sign_in")}
							</Button>
							<FormControlErrorMessage
								name="login-form"
								hidden={!isFormErrorActive}
								className="text-center"
							>
								{actionData?.error?.errorType === "unverifiedEmail" ? (
									<>
										{t("login:email_not_verified_error")}{" "}
										<Button
											variant="text"
											size="small"
											type="submit"
											name="intent"
											value="resend-verification-email"
											className="text-current underline"
											isDisabled={navigation.state !== "idle"}
											testId="resend-verification-email-button"
										>
											{t("login:resend_verification_email_button_label")}
										</Button>
										.
									</>
								) : (
									actionData?.error?.message
								)}
							</FormControlErrorMessage>
							<Button
								variant="text"
								href={`/forgot-password${
									email ? `?email=${encodeURIComponent(email)}` : ""
								}`}
								size="small"
								className="text-text-secondary mt-2 font-normal underline"
							>
								{t("login:forgot_password_link_label")}
							</Button>
							{/* <Button
								variant="text"
								href="/signup"
								size="small"
								className="text-text-secondary font-normal  underline"
							>
								{t("login:go_to_signup_link_label")}
							</Button> */}
						</fieldset>
					</Form>
				</div>
			</div>
		</section>
	);
}

export function ErrorBoundary(_props: Route.ErrorBoundaryProps) {
	return <GeneralErrorBoundary />;
}
